import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { logoutAction } from 'store/login/loginAction';
import { API_URL } from 'utils';
import { AuthStore, AuthData } from 'modules/common/PortfolioManager/types';
import { ErrorMessage } from 'shared/types/errorMessage';
import { FUNDS_URL, NAVIGATOR_URL } from '@scope/scope-one-common/utils/apiUrl';

enum Status {
  AUTH_TOKEN_EXPIRED = 401,
}

export type ApiPrefix = 'api' | 'navigator' | 'fund-analysis';

export type RequestOptions<P = any> = RequestInit & {
  /* Request body, that will be converted to JSON */
  payload?: P;

  /* Expected response type, 'json' by default. */
  response?: 'json' | 'blob' | 'text' | 'raw';

  /* URL query params, joined with 'qs'. */
  params?: Record<string, any>;

  /* Custom error message */
  errorMessage?: string;

  /* Sets api prefix to proxy for given module */
  apiPrefix?: ApiPrefix;

  /* Prevents logout action after 401 code returned */
  disableLogout?: boolean;

  /* Disables refetch on params change. Requires `refetch` to be called manually. */
  disableAutofetch?: boolean;
};

export const getErrorMessage = (error: string) => {
  try {
    return JSON.parse(error).message;
  } catch {
    return 'Something went wrong.';
  }
};

export const useAuth = () => useSelector<AuthStore, AuthData>(({ auth }) => auth.data);

export const useApi = () => {
  const { token } = useAuth();
  const dispatch = useDispatch();

  const defaultHeaders = {
    'Content-Type': 'application/json',
    authorization: `Bearer ${token}`,
  };

  const makeMethod =
    (method: string) =>
    <R, P = void>(url: string, options: RequestOptions<P> = {}): Promise<R> => {
      const {
        payload,
        response = 'json',
        params,
        errorMessage,
        headers,
        apiPrefix = 'api',
        disableLogout,
      } = options;

      const body = payload ? { body: JSON.stringify(payload) } : {};

      const queryParams = qs.stringify(params, { skipNulls: true });

      const query = queryParams ? `?${queryParams}` : '';

      const init = {
        headers: {
          ...defaultHeaders,
          ...headers,
        },
        method,
        ...body,
      };

      const prefixes: Record<ApiPrefix, string> = {
        api: API_URL,
        navigator: NAVIGATOR_URL,
        'fund-analysis': FUNDS_URL,
      };

      return fetch(`${prefixes[apiPrefix]}${url}${query}`, init).then((res) => {
        if (!disableLogout && res.status === Status.AUTH_TOKEN_EXPIRED) {
          dispatch(logoutAction);
        }

        if (!res.ok) {
          return res.text().then((err) => {
            throw new Error(
              JSON.stringify({
                code: res.status,
                status: res.statusText,
                message: errorMessage || getErrorMessage(err),
              } as ErrorMessage),
            );
          });
        }

        if (response === 'raw') return res;
        if (response === 'blob') return res.blob().then(URL.createObjectURL);
        if (response === 'text') return res.text();
        return res.json();
      });
    };

  return {
    get: makeMethod('GET'),
    post: makeMethod('POST'),
    put: makeMethod('PUT'),
    patch: makeMethod('PATCH'),
    del: makeMethod('DELETE'),
    upload: makeMethod('UPLOAD'),
  };
};
